import { useState, useEffect } from "react";
import {
  X,
  Check,
  RotateCcw,
  Trash2,
  Plus,
  Pencil,
  BookOpen,
  HelpCircle,
  FileText,
  Loader2,
  AlertCircle,
  Save,
  EyeOff,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { MathText } from "../MathText";
import { ConfirmDialog } from "./AdminModals";
import { AdminPassageSelector } from "./AdminPassageSelector";
import { Modal } from "../ui/Modal";

interface DraftQuestion {
  id: string;
  question: string;
  options: string[];
  answer: number;
  explanation: string;
  difficulty?: string;
  passageId?: string | null;
  passage_id?: string | null;
}

interface DraftNote {
  id: string;
  topic_title: string;
  markdown: string;
  key_terms?: string[];
}

interface DraftChapter {
  id: string;
  title: string;
  sortOrder: number;
  topics?: Array<{ id: string; title: string }>;
  notes?: DraftNote[];
  questions?: DraftQuestion[];
}

interface DraftData {
  moduleId: string;
  moduleTitle: string;
  courseName?: string | null;
  status: string;
  error?: string | null;
  chapters: DraftChapter[];
}

interface Props {
  open: boolean;
  moduleId: string | null;
  token: string;
  onClose: () => void;
  onUpdated?: () => void;
}

export function AdminModuleDraftModal({ open, moduleId, token, onClose, onUpdated }: Props) {
  const [data, setData] = useState<DraftData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"chapters" | "notes" | "questions">("chapters");
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(0);
  const [collapsedChapters, setCollapsedChapters] = useState<Set<number>>(new Set());
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [deleteChapterIndex, setDeleteChapterIndex] = useState<number | null>(null);

  // Edit question modal / state
  const [editingQuestion, setEditingQuestion] = useState<{
    chapterIndex: number;
    qIndex: number;
    q: DraftQuestion;
  } | null>(null);

  // Edit chapter modal / state
  const [editingChapter, setEditingChapter] = useState<{
    chapterIndex: number;
    title: string;
  } | null>(null);

  // Edit note state
  const [editingNote, setEditingNote] = useState<{
    chapterIndex: number;
    noteIndex: number;
    markdown: string;
  } | null>(null);

  // Rejection modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const loadDraft = async () => {
    if (!moduleId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.getModuleDraft(token, moduleId);
      const rawChapters = (res as any)?.chapters || (res as any)?.content?.chapters || [];
      const normalizedData: DraftData = {
        moduleId: (res as any)?.moduleId || moduleId,
        moduleTitle: (res as any)?.moduleTitle || (res as any)?.title || "Review Module Extraction Draft",
        courseName: (res as any)?.courseName,
        status: (res as any)?.status || "extracted",
        error: (res as any)?.error || (res as any)?.errorMessage,
        chapters: Array.isArray(rawChapters) ? rawChapters : [],
      };
      setData(normalizedData);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load module draft");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && moduleId) {
      loadDraft();
    } else {
      setData(null);
      setError(null);
    }
  }, [open, moduleId, token]);

  if (!open) return null;

  const currentChapter = data?.chapters?.[selectedChapterIndex];

  const handleSaveDraft = async () => {
    if (!data || !moduleId || !token) return;
    setSaving(true);
    setError(null);
    try {
      await api.admin.updateModuleDraft(token, moduleId, {
        chapters: data.chapters,
      });
      onUpdated?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save draft changes");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!moduleId || !token) return;
    setActionLoading("publish");
    setError(null);
    try {
      await api.admin.publishModuleExtraction(token, moduleId);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to publish module");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!moduleId || !token) return;
    setActionLoading("reject");
    setError(null);
    try {
      await api.admin.rejectModuleExtraction(token, moduleId, rejectReason);
      setShowRejectModal(false);
      setRejectReason("");
      onUpdated?.();
      await loadDraft();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to reject draft");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async () => {
    if (!moduleId || !token) return;
    setActionLoading("unpublish");
    setError(null);
    try {
      await api.admin.unpublishModuleExtraction(token, moduleId);
      onUpdated?.();
      await loadDraft();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to unpublish module");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerate = () => {
    setConfirmRegenerate(true);
  };

  const executeRegenerate = async () => {
    if (!moduleId || !token) return;
    setActionLoading("regenerate");
    setError(null);
    try {
      await api.admin.regenerateModuleExtraction(token, moduleId);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to restart extraction");
    } finally {
      setActionLoading(null);
      setConfirmRegenerate(false);
    }
  };

  const handleDeleteQuestion = (chIdx: number, qIdx: number) => {
    if (!data) return;
    const chapters = [...data.chapters];
    const qs = [...(chapters[chIdx].questions || [])];
    qs.splice(qIdx, 1);
    chapters[chIdx] = { ...chapters[chIdx], questions: qs };
    setData({ ...data, chapters });
  };

  const handleDeleteChapter = (chIdx: number) => {
    setDeleteChapterIndex(chIdx);
  };

  const executeDeleteChapter = (chIdx: number) => {
    if (!data) return;
    const chapters = data.chapters.filter((_, i) => i !== chIdx);
    setData({ ...data, chapters });
    if (selectedChapterIndex >= chapters.length) {
      setSelectedChapterIndex(Math.max(0, chapters.length - 1));
    }
    setDeleteChapterIndex(null);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      className="p-0 overflow-hidden max-w-6xl h-[92vh]"
    >
      <div className="w-full h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-foreground">
                {data?.moduleTitle || "Review Module Extraction Draft"}
              </h2>
              {data && (
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${
                  data.status === "published"
                    ? "bg-success/10 text-success"
                    : data.status === "extracted"
                    ? "bg-warning/10 text-warning"
                    : data.status === "rejected"
                    ? "bg-destructive/10 text-destructive"
                    : "bg-info/10 text-info"
                }`}>
                  {data.status === "extracted" ? "Awaiting Review" : data.status.toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Draft extracted content is strictly hidden from students until published.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving || loading}
              className="px-3 py-1.5 bg-secondary text-primary hover:bg-secondary/80 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Draft Edits
            </button>

            {data?.status === "published" ? (
              <button
                type="button"
                onClick={handleUnpublish}
                disabled={actionLoading === "unpublish"}
                className="px-3 py-1.5 bg-warning hover:bg-warning text-warning-foreground rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50"
              >
                {actionLoading === "unpublish" ? <Loader2 size={13} className="animate-spin" /> : <EyeOff size={13} />}
                Unpublish
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading === "reject" || loading}
                  className="px-3 py-1.5 border border-destructive/30 text-destructive hover:bg-destructive/10 rounded-lg text-xs font-semibold transition disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={actionLoading === "publish" || loading}
                  className="px-3 py-1.5 bg-success hover:bg-success text-success-foreground rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50 shadow-xs"
                >
                  {actionLoading === "publish" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  Approve &amp; Publish
                </button>
              </>
            )}

            <button
              type="button"
              onClick={handleRegenerate}
              disabled={actionLoading === "regenerate" || loading}
              className="px-3 py-1.5 border border-border text-foreground hover:bg-muted rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
              title="Re-run AI extraction"
            >
              {actionLoading === "regenerate" ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              Regenerate
            </button>

            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-xs font-semibold transition ml-2"
            >
              Back to modules
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-xs text-destructive flex items-center gap-2">
            <AlertCircle size={14} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <Loader2 size={28} className="animate-spin text-primary" />
            <p className="text-sm">Loading module draft content…</p>
          </div>
        ) : !data || !data.chapters || data.chapters.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
            <AlertCircle size={32} className="text-muted-foreground mb-2" />
            <p className="text-sm font-semibold">No draft chapters found.</p>
            <p className="text-xs text-muted-foreground max-w-sm mt-1">
              Start extraction or click Regenerate to extract chapters, notes, and questions with AI.
            </p>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar Chapter Navigator */}
            <div className="w-72 border-r border-border bg-muted/20 flex flex-col overflow-y-auto">
              <div className="p-3 border-b border-border bg-muted/40 flex items-center justify-between">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Chapters ({data.chapters?.length || 0})
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const currentChapters = data.chapters || [];
                    const newChapter: DraftChapter = {
                      id: `temp-${Date.now()}`,
                      title: `Chapter ${currentChapters.length + 1}`,
                      sortOrder: currentChapters.length + 1,
                      topics: [],
                      notes: [],
                      questions: [],
                    };
                    setData({ ...data, chapters: [...currentChapters, newChapter] });
                    setSelectedChapterIndex(currentChapters.length);
                  }}
                  className="text-xs text-primary font-semibold hover:underline flex items-center gap-0.5"
                >
                  <Plus size={12} /> Add
                </button>
              </div>

              <div className="divide-y divide-border/60 p-2 space-y-1">
                {(data.chapters || []).map((ch, idx) => {
                  const qCount = ch.questions?.length || 0;
                  const isSelected = selectedChapterIndex === idx;
                  return (
                    <div
                      key={ch.id || idx}
                      onClick={() => setSelectedChapterIndex(idx)}
                      className={`group p-2.5 rounded-lg cursor-pointer text-xs transition flex items-start justify-between ${
                        isSelected
                          ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                          : "hover:bg-muted text-foreground"
                      }`}
                    >
                      <div className="flex-1 pr-2 truncate">
                        <div className="truncate">{ch.title}</div>
                        <div className={`text-xs mt-0.5 ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                          {qCount} question{qCount === 1 ? "" : "s"} · {ch.notes?.length || 0} notes
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteChapter(idx);
                        }}
                        className={`opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-black/10 transition ${
                          isSelected ? "text-primary-foreground" : "text-destructive"
                        }`}
                        title="Delete chapter"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Main Content Details */}
            <div className="flex-1 flex flex-col overflow-hidden bg-background">
              {currentChapter && (
                <>
                  {/* Chapter Header Tabs */}
                  <div className="px-6 py-3 border-b border-border flex items-center justify-between bg-muted/10">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                        <span>{currentChapter.title}</span>
                        <button
                          type="button"
                          onClick={() => setEditingChapter({ chapterIndex: selectedChapterIndex, title: currentChapter.title })}
                          className="text-muted-foreground hover:text-foreground"
                          title="Rename chapter"
                        >
                          <Pencil size={12} />
                        </button>
                      </h3>
                    </div>

                    <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5 text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => setActiveTab("chapters")}
                        className={`px-3 py-1 rounded-md transition ${activeTab === "chapters" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Topics &amp; Structure
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab("notes")}
                        className={`px-3 py-1 rounded-md transition ${activeTab === "notes" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Short Notes ({currentChapter.notes?.length || 0})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab("questions")}
                        className={`px-3 py-1 rounded-md transition ${activeTab === "questions" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Question Bank ({currentChapter.questions?.length || 0})
                      </button>
                    </div>
                  </div>

                  {/* Tab Body */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {/* Structure Tab */}
                    {activeTab === "chapters" && (
                      <div className="space-y-4 max-w-3xl">
                        <div>
                          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                            Chapter Title
                          </label>
                          <input
                            type="text"
                            value={currentChapter.title}
                            onChange={(e) => {
                              const chapters = [...data.chapters];
                              chapters[selectedChapterIndex] = { ...currentChapter, title: e.target.value };
                              setData({ ...data, chapters });
                            }}
                            className="mt-1 w-full px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-hidden focus:ring-1 focus:ring-primary"
                          />
                        </div>

                        <div>
                          <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                            Topics
                          </label>
                          <div className="mt-2 space-y-2">
                            {(currentChapter.topics || []).map((t, tIdx) => (
                              <div key={t.id || tIdx} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={t.title}
                                  onChange={(e) => {
                                    const topics = [...(currentChapter.topics || [])];
                                    topics[tIdx] = { ...t, title: e.target.value };
                                    const chapters = [...data.chapters];
                                    chapters[selectedChapterIndex] = { ...currentChapter, topics };
                                    setData({ ...data, chapters });
                                  }}
                                  className="flex-1 px-3 py-1.5 text-xs bg-card border border-border rounded-md focus:outline-hidden focus:ring-1 focus:ring-primary"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const topics = (currentChapter.topics || []).filter((_, i) => i !== tIdx);
                                    const chapters = [...data.chapters];
                                    chapters[selectedChapterIndex] = { ...currentChapter, topics };
                                    setData({ ...data, chapters });
                                  }}
                                  className="text-destructive hover:text-destructive p-1"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const topics = [...(currentChapter.topics || []), { id: `topic-${Date.now()}`, title: "New Topic" }];
                                const chapters = [...data.chapters];
                                chapters[selectedChapterIndex] = { ...currentChapter, topics };
                                setData({ ...data, chapters });
                              }}
                              className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 mt-2"
                            >
                              <Plus size={12} /> Add Topic
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Short Notes Tab */}
                    {activeTab === "notes" && (
                      <div className="space-y-6 max-w-3xl">
                        {(currentChapter.notes || []).length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground text-xs">
                            No short notes generated for this chapter.
                            <br />
                            <button
                              type="button"
                              onClick={() => {
                                const newNote: DraftNote = {
                                  id: `note-${Date.now()}`,
                                  topic_title: currentChapter.title,
                                  markdown: "### Key Concepts\n\n- Summary points here...",
                                  key_terms: [],
                                };
                                const chapters = [...data.chapters];
                                chapters[selectedChapterIndex] = {
                                  ...currentChapter,
                                  notes: [newNote],
                                };
                                setData({ ...data, chapters });
                              }}
                              className="mt-2 text-primary font-semibold hover:underline inline-flex items-center gap-1"
                            >
                              <Plus size={12} /> Add Note Section
                            </button>
                          </div>
                        ) : (
                          (currentChapter.notes || []).map((note, nIdx) => (
                            <div key={note.id || nIdx} className="border border-border rounded-xl p-4 bg-card shadow-xs">
                              <div className="flex items-center justify-between mb-3 border-b border-border/60 pb-2">
                                <h4 className="text-xs font-bold text-foreground">{note.topic_title}</h4>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setEditingNote({
                                      chapterIndex: selectedChapterIndex,
                                      noteIndex: nIdx,
                                      markdown: note.markdown,
                                    })}
                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                  >
                                    <Pencil size={11} /> Edit Markdown
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const notes = (currentChapter.notes || []).filter((_, i) => i !== nIdx);
                                      const chapters = [...data.chapters];
                                      chapters[selectedChapterIndex] = { ...currentChapter, notes };
                                      setData({ ...data, chapters });
                                    }}
                                    className="text-xs text-destructive hover:text-destructive p-1"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>

                              <div className="prose prose-xs max-w-none text-xs text-foreground">
                                <MathText>{note.markdown}</MathText>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Question Bank Tab */}
                    {activeTab === "questions" && (
                      <div className="space-y-4 max-w-4xl">
                        <div className="flex items-center justify-between pb-2 border-b border-border">
                          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                            Questions ({currentChapter.questions?.length || 0})
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const newQ: DraftQuestion = {
                                id: `q-${Date.now()}`,
                                question: "Enter question stem here...",
                                options: ["Option A", "Option B", "Option C", "Option D"],
                                answer: 0,
                                explanation: "Explanation for the correct answer.",
                                difficulty: "Medium",
                              };
                              const chapters = [...data.chapters];
                              chapters[selectedChapterIndex] = {
                                ...currentChapter,
                                questions: [...(currentChapter.questions || []), newQ],
                              };
                              setData({ ...data, chapters });
                            }}
                            className="px-2.5 py-1 bg-primary text-primary-foreground rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-primary/90"
                          >
                            <Plus size={12} /> Add Question
                          </button>
                        </div>

                        {(currentChapter.questions || []).map((q, qIdx) => (
                          <div key={q.id || qIdx} className="border border-border rounded-xl p-4 bg-card shadow-xs">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-secondary text-primary text-xs font-bold flex items-center justify-center">
                                  {qIdx + 1}
                                </span>
                                <span className="text-xs uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {q.difficulty || "Medium"}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingQuestion({
                                    chapterIndex: selectedChapterIndex,
                                    qIndex: qIdx,
                                    q: { ...q },
                                  })}
                                  className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                  <Pencil size={11} /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteQuestion(selectedChapterIndex, qIdx)}
                                  className="text-xs text-destructive hover:text-destructive p-1"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>

                            {/* Question text with LaTeX rendering */}
                            <div className="text-xs font-semibold text-foreground mb-3 leading-relaxed">
                              <MathText>{q.question}</MathText>
                            </div>

                            {/* Options */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              {(q.options || []).map((opt, oIdx) => {
                                const isCorrect = q.answer === oIdx;
                                return (
                                  <div
                                    key={oIdx}
                                    className={`p-2 rounded-lg text-xs flex items-center gap-2 border ${
                                      isCorrect
                                        ? "bg-success/10 border-success/30 text-success font-medium"
                                        : "bg-muted/40 border-border text-foreground"
                                    }`}
                                  >
                                    <span className="font-bold uppercase text-xs">
                                      {String.fromCharCode(65 + oIdx)}.
                                    </span>
                                    <div className="flex-1 truncate">
                                      <MathText inline>{opt}</MathText>
                                    </div>
                                    {isCorrect && <Check size={12} className="text-success shrink-0" />}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Explanation */}
                            {q.explanation && (
                              <div className="p-2.5 bg-muted/30 rounded-lg text-xs text-muted-foreground border border-border/40">
                                <span className="font-bold text-foreground">Explanation: </span>
                                <MathText inline>{q.explanation}</MathText>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Edit Question Dialog */}
        <Modal
          isOpen={!!editingQuestion}
          onClose={() => setEditingQuestion(null)}
          title="Edit Question"
          size="md"
        >
          {editingQuestion && (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto admin-scroll-y">

              <div>
                <label className="text-xs font-bold text-muted-foreground">Question Stem (LaTeX: $...$ or $$...$$)</label>
                <textarea
                  rows={3}
                  value={editingQuestion.q.question}
                  onChange={(e) => setEditingQuestion({
                    ...editingQuestion,
                    q: { ...editingQuestion.q, question: e.target.value },
                  })}
                  className="mt-1 w-full p-2 text-xs bg-muted/40 border border-border rounded-md font-mono"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-muted-foreground">Options</label>
                <div className="space-y-2 mt-1">
                  {editingQuestion.q.options.map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={editingQuestion.q.answer === optIdx}
                        onChange={() => setEditingQuestion({
                          ...editingQuestion,
                          q: { ...editingQuestion.q, answer: optIdx },
                        })}
                        title="Mark as correct"
                      />
                      <span className="text-xs font-bold w-4">{String.fromCharCode(65 + optIdx)}</span>
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                          const opts = [...editingQuestion.q.options];
                          opts[optIdx] = e.target.value;
                          setEditingQuestion({
                            ...editingQuestion,
                            q: { ...editingQuestion.q, options: opts },
                          });
                        }}
                        className="flex-1 p-1.5 text-xs bg-muted/40 border border-border rounded-md"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <AdminPassageSelector
                scope={currentChapter?.id ? `quiz:${currentChapter.id}` : `quiz:${moduleId}`}
                selectedPassageId={editingQuestion.q.passageId || editingQuestion.q.passage_id || null}
                onSelectPassage={(pId) => {
                  setEditingQuestion({
                    ...editingQuestion,
                    q: {
                      ...editingQuestion.q,
                      passageId: pId,
                      passage_id: pId,
                    },
                  });
                }}
                token={token}
              />

              <div>
                <label className="text-xs font-bold text-muted-foreground">Explanation</label>
                <textarea
                  rows={2}
                  value={editingQuestion.q.explanation}
                  onChange={(e) => setEditingQuestion({
                    ...editingQuestion,
                    q: { ...editingQuestion.q, explanation: e.target.value },
                  })}
                  className="mt-1 w-full p-2 text-xs bg-muted/40 border border-border rounded-md font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setEditingQuestion(null)}
                  className="px-3 py-1.5 text-xs border border-border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!data) return;
                    const chapters = [...data.chapters];
                    const qs = [...(chapters[editingQuestion.chapterIndex].questions || [])];
                    qs[editingQuestion.qIndex] = editingQuestion.q;
                    chapters[editingQuestion.chapterIndex] = {
                      ...chapters[editingQuestion.chapterIndex],
                      questions: qs,
                    };
                    setData({ ...data, chapters });
                    setEditingQuestion(null);
                  }}
                  className="px-3 py-1.5 text-xs bg-primary text-primary-foreground font-semibold rounded-md"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit Note Markdown Dialog */}
        <Modal
          isOpen={!!editingNote}
          onClose={() => setEditingNote(null)}
          title="Edit Note Section Markdown"
          size="lg"
        >
          {editingNote && (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto admin-scroll-y">

              <div>
                <label className="text-xs font-bold text-muted-foreground">Markdown Content (LaTeX: $...$ or $$...$$)</label>
                <textarea
                  rows={12}
                  value={editingNote.markdown}
                  onChange={(e) => setEditingNote({ ...editingNote, markdown: e.target.value })}
                  className="mt-1 w-full p-2.5 text-xs bg-muted/40 border border-border rounded-md font-mono leading-relaxed"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setEditingNote(null)}
                  className="px-3 py-1.5 text-xs border border-border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!data) return;
                    const chapters = [...data.chapters];
                    const notes = [...(chapters[editingNote.chapterIndex].notes || [])];
                    notes[editingNote.noteIndex] = {
                      ...notes[editingNote.noteIndex],
                      markdown: editingNote.markdown,
                    };
                    chapters[editingNote.chapterIndex] = {
                      ...chapters[editingNote.chapterIndex],
                      notes,
                    };
                    setData({ ...data, chapters });
                    setEditingNote(null);
                  }}
                  className="px-3 py-1.5 text-xs bg-primary text-primary-foreground font-semibold rounded-md"
                >
                  Save Note
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Rejection Prompt Dialog */}
        <Modal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          title="Reject Draft Extraction"
          description="Provide an optional reason for rejecting this extraction draft:"
          size="sm"
        >
          <div className="space-y-3">
            <textarea
              rows={3}
              placeholder="e.g. Incomplete chapters, low quality questions..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full p-2 text-xs bg-muted/40 border border-border rounded-md focus:outline-hidden focus:ring-1 focus:ring-primary"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-muted cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={actionLoading === "reject"}
                className="px-3 py-1.5 text-xs bg-destructive hover:bg-destructive text-destructive-foreground font-semibold rounded-md transition disabled:opacity-50 cursor-pointer"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </Modal>

        <ConfirmDialog
          open={confirmRegenerate}
          title="Regenerate extraction?"
          message="This will re-trigger AI extraction from the source document, replacing current draft edits."
          confirmLabel="Regenerate"
          variant="destructive"
          loading={actionLoading === "regenerate"}
          onConfirm={executeRegenerate}
          onClose={() => setConfirmRegenerate(false)}
        />

        <ConfirmDialog
          open={deleteChapterIndex !== null}
          title="Delete chapter?"
          message={
            deleteChapterIndex !== null && data?.chapters[deleteChapterIndex]
              ? `Are you sure you want to delete "${data.chapters[deleteChapterIndex].title}" and all its questions and notes?`
              : "Are you sure you want to delete this chapter?"
          }
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={() => {
            if (deleteChapterIndex !== null) executeDeleteChapter(deleteChapterIndex);
          }}
          onClose={() => setDeleteChapterIndex(null)}
        />
      </div>
    </Modal>
  );
}
